import type { CmmdBoardTask } from "@cmmd-forge/contracts";
import type { SidebarThreadSummary } from "~/types";
import { resolveTaskThreadFromIndex, type ResolvedTaskThread } from "~/lib/taskThreadLink";
import {
  formatPriorityToken,
  type PriorityToken,
} from "~/components/command-center/taskRowFormatting";

export interface TaskPresentation {
  readonly priorityToken: PriorityToken | null;
  readonly assigneeName: string | null;
  readonly assigneeAvatar: string | null;
  readonly assigneeKind: "human" | "agent" | null;
  readonly assigneeAgentId: string | null;
  readonly dueLabel: string | null;
  readonly isBlocked: boolean;
  readonly subtaskCount: number;
  readonly resolvedThread: ResolvedTaskThread | null;
}

export function formatTaskDueLabel(dueDate: string | null): string | null {
  if (!dueDate) return null;
  const ms = Date.parse(dueDate);
  if (!Number.isFinite(ms)) return null;
  return new Date(ms).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    timeZone: /^\d{4}-\d{2}-\d{2}(?:T00:00:00(?:\.000)?Z)?$/.test(dueDate) ? "UTC" : undefined,
  });
}

export function taskAssigneeName(task: CmmdBoardTask): string | null {
  return task.assignment?.kind === "agent_member" ? task.assignment.displayName : task.assigneeName;
}

/**
 * Pure presentation view-model shared by ProjectTaskRow and ProjectTaskCard so
 * the title/priority/assignee/due/agent-badge derivation lives in one place
 * (F8). `threadIndex` is the prebuilt task→thread reverse index.
 */
export function deriveTaskPresentation(
  task: CmmdBoardTask,
  threadIndex: ReadonlyMap<string, SidebarThreadSummary>,
): TaskPresentation {
  return {
    priorityToken: formatPriorityToken(task.priority),
    assigneeName: taskAssigneeName(task),
    assigneeAvatar: task.assignment?.kind === "agent_member" ? null : task.assigneeAvatar,
    assigneeKind:
      task.assignment?.kind === "agent_member"
        ? "agent"
        : task.assignment?.kind === "human_user"
          ? "human"
          : null,
    assigneeAgentId: task.assignment?.kind === "agent_member" ? task.assignment.agentId : null,
    dueLabel: formatTaskDueLabel(task.dueDate),
    isBlocked: task.dependsOn.length > 0,
    subtaskCount: task.childCount,
    resolvedThread: resolveTaskThreadFromIndex(threadIndex, task.id),
  };
}
